import { useEffect, useMemo, useState } from "react"
import { useEmployeFilter } from "../../../context/EmployeFilterContext";
import services from "../../../services/services";
import { Urls } from "../../../services/Urls";
import _ from "underscore";
import { getMonthName } from "../../../utils/Helper";

export const useTechExpCount = () => {

    const { selectedEmployee } = useEmployeFilter();
    const [data, setData] = useState({});
    const [categoryData, setCategoryData] = useState({});
    const [viewYearly, setViewYearly] = useState(false);

    useEffect(() => {
        selectedEmployee?.user_code && getData();
    }, [selectedEmployee?.user_code])

    useEffect(() => {
        data?.user_involvement_counts && formatYearlyData(data);
    }, [viewYearly, data])

    const getData = async () => {
        try {
            const payload = {
                project_lead_user_code: selectedEmployee?.user_code,
            }
            const res = await services.getInvokeRemote("POST", Urls("GETTEAMATTRIBUTESCOUNTSUMMARY"), payload);
            if (res) {
                const data = res?.body?.data;
                formatYearlyData(data);
                setData(data);
            }
        } catch (error) {
            console.error("Error: ", error);
        }
    }

    const formatYearlyData = (data) => {
        const currentMonth = new Date()?.getMonth();
        let categoryXAxis = [];
        let monthlySeriesData = [];
        let yearlySeriesData = [];

        const currentMonthData = _.findWhere(data?.user_involvement_counts, { report_month: currentMonth })
        const keys = _.filter(Object.keys(currentMonthData), (key) => key !== "report_year" && key !== "report_month");

        if (viewYearly) {
            keys?.forEach(key => {
                const seriesName = key?.split("_")?.join(" ")?.charAt(0)?.toUpperCase() + key?.split("_")?.join(" ")?.slice(1)
                yearlySeriesData.push({
                    name: seriesName,
                    data: _.pluck(data?.user_involvement_counts, key)
                })
            })
            data?.user_involvement_counts?.forEach((item) => {
                categoryXAxis?.push(getMonthName(item?.report_month));
            })
        } else {
            categoryXAxis = keys?.map(item => item?.split("_")?.join(" "))
            categoryXAxis?.forEach(item => {
                monthlySeriesData?.push(currentMonthData?.[item?.split(" ")?.join("_")])
            })
            categoryXAxis = categoryXAxis?.map(item => {return item?.charAt(0)?.toUpperCase() + item?.slice(1)})
        }
        setCategoryData({ x_axis: categoryXAxis, monthlySeriesData, yearlySeriesData })
    }

    const techWiseOptions = {
        credits: { enabled: false },
        chart: {
            type: 'column'
        },
        title: {
            text: ''
        },
        xAxis: {
            categories: _.pluck(_.sortBy(data?.technology_counts, "tech_user_counts")?.reverse(), "primary_skill_1"),
            labels: {
                enabled: true,
                step: 1,
                style: {
                    fontSize: '10px'
                },
                rotation: _.pluck(data?.technology_counts, "primary_skill_1")?.length > 6 ? -45 : 0
            }
        },
        yAxis: {
            min: 0,
            max: _.max(_.pluck(data?.technology_counts, "tech_user_counts")),
            title: "",
            visible: false,
        },
        tooltip: {
            headerFormat: '<span style="font-size:10px">{point.key}</span><table>',
            pointFormat: '<tr><td style="color:{series.color};padding:0">{series.name}: </td>' +
                '<td style="padding:0"><b>{point.y}</b></td></tr>',
            footerFormat: '</table>',
            shared: true,
            useHTML: true
        },
        plotOptions: {
            column: {
                allowPointSelect: true,
                cursor: 'pointer',
                dataLabels: {
                    enabled: true
                },
                showInLegend: false,
                colorByPoint: true,
                pointPadding: 0.05,
                groupPadding: 0.05,
                borderWidth: 0
            }
        },
        series: [
            {
                name: "Employees",
                data: _.pluck(_.sortBy(data?.technology_counts, "tech_user_counts")?.reverse(), "tech_user_counts")
            }
        ]
    }

    const expWiseOptions = {
        credits: { enabled: false },
        chart: {
            type: 'area'
        },
        title: {
            text: ''
        },
        xAxis: {
            categories: _.pluck(data?.experience_range_counts, "experience_range"),
            labels: {
                enabled: true,
                step: 1,
                style: {
                    fontSize: '10px'
                },
            }
        },
        yAxis: {
            min: 0,
            title: "",
            visible: false
        },
        plotOptions: {
            area: {
                allowPointSelect: true,
                cursor: 'pointer',
                dataLabels: {
                    enabled: true
                },
                showInLegend: false,
            }
        },
        series: [
            {
                name: "Employees",
                data: _.pluck(data?.experience_range_counts, "user_count"),
                color: {
                    linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
                    stops: [
                        [0, 'rgba(44, 128, 45, 0.7)'],
                        [1, 'rgba(64, 155, 67, 0.34)']
                    ]
                },
                lineColor: '#2f8033ff',
            }
        ]
    }

    const categoryWiseOptions = {
        credits: { enabled: false },
        chart: {
            type: 'column'
        },
        title: {
            text: ''
        },
        xAxis: {
            categories: categoryData?.x_axis || [],
            labels: {
                enabled: true,
                step: 1,
                style: {
                    fontSize: '12px',
                },
            }
        },
        yAxis: {
            min: 0,
            max: _.max(categoryData?.monthlySeriesData),
            title: "",
            visible: false,
        },
        tooltip: {
            headerFormat: '<span style="font-size:10px">{point.key}</span><table>',
            pointFormat: '<tr><td style="color:{series.color};padding:0">{series.name}: </td>' +
                '<td style="padding:0"><b>{point.y}</b></td></tr>',
            footerFormat: '</table>',
            shared: true,
            useHTML: true
        },
        plotOptions: {
            column: {
                allowPointSelect: true,
                cursor: 'pointer',
                dataLabels: {
                    enabled: true
                },
                showInLegend: false,
                colorByPoint: true,
                pointPadding: 0.05,
                groupPadding: 0.05,
                borderWidth: 0
            }
        },
        series: [
            {
                name: "Employees",
                data: categoryData?.monthlySeriesData
            }
        ]
    }

    const yearlyCategoryWiseOptions = {
        credits: { enabled: false },
        chart: {
            type: 'line'
        },
        title: {
            text: ''
        },
        xAxis: {
            categories: categoryData?.x_axis,
            labels: {
                enabled: true,
                step: 1,
                style: {
                    fontSize: '10px'
                },
            }
        },
        yAxis: {
            min: 0,
            title: "",
            visible: false,
        },
        tooltip: {
            headerFormat: '<span style="font-size:10px">{point.key}</span><table>',
            pointFormat: '<tr><td style="color:{series.color};padding:0">{series.name}: </td>' +
                '<td style="padding:0"><b>{point.y}</b></td></tr>',
            footerFormat: '</table>',
            shared: true,
            useHTML: true
        },
        plotOptions: {
            line: {
                allowPointSelect: true,
                cursor: 'pointer',
                dataLabels: {
                    enabled: true,
                    y: -15,
                    style: { fontSize: "10px" }
                },
                showInLegend: true,
            }
        },
        legend: {
            width: 80,
            borderWidth: 0,
            floating: 0,
            layout: 'vertical',
            align: 'right',
            verticalAlign: 'top',
            x: 0,
            y: 0,
        },
        series: categoryData?.yearlySeriesData
    }

    const attributes = [
        { title: "Tech-wise Team Count", options: techWiseOptions, width: "40%", height: "85%", show_btn: false },
        { title: "Exp-wise Team Count", options: expWiseOptions, width: "25%", height: "85%", show_btn: false },
        { title: "Category-wise Team Count", options: viewYearly ? yearlyCategoryWiseOptions : categoryWiseOptions, width: "35%", height: "70%", show_btn: true },
    ]

    return useMemo(() => ({
        attributes, setViewYearly
    }), [attributes, setViewYearly])
}